import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Subject, takeUntil } from 'rxjs';
import { CookieConsentStateService } from './cookie-consent-state.service';
import { COOKIE_CATEGORIES } from './cookie-categories';
import { CookieCategoryId, CookieChoiceId, GmCookieConsent } from '../../domain/cookie-consent.model';
import { IonicModule } from '@ionic/angular';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

const CONSENT_KEY = 'gm_cookie_consent';
const CONSENT_VERSION = 3;
const GA_ID = 'G-F9CZE8FSZ1';
/** Taps on a consent button this soon after the banner or a new view appears are ignored:
 *  the layout changes under a finger that is still on its way (a double-tap on "Anpassen"
 *  lands on the settings footer's buttons), and that must never count as a decision. */
const DECISION_GUARD_MS = 500;

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('ccSlide', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms cubic-bezier(0.16, 1, 0.3, 1)', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('220ms cubic-bezier(0.4, 0, 1, 1)', style({ transform: 'translateY(100%)', opacity: 0 })),
      ]),
    ]),
    trigger('ccFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('160ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
  imports: [
    CdkTrapFocus,
    IonicModule,
    RouterLink,
    TranslatePipe,
  ],
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  readonly categories = COOKIE_CATEGORIES;
  readonly reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  visible = false;
  expanded = false;

  readonly choices: Record<CookieChoiceId, boolean> = { functional: false, analytics: false };

  private readonly openDetails = new Set<CookieCategoryId>();
  /** Set when the settings view was reopened after a consent already exists: closing it
   *  must dismiss the banner instead of falling back to the first-visit prompt. */
  private hasDecision = false;
  /** Whatever had focus when the settings were reopened, so a dismissal can hand focus back. */
  private returnFocusTo: HTMLElement | null = null;
  private decisionsArmedAt = 0;

  @ViewChild('customizeBtn') private readonly customizeBtn?: ElementRef<HTMLElement>;
  @ViewChild('settingsTitle') private readonly settingsTitle?: ElementRef<HTMLElement>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly consentState: CookieConsentStateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Reopen request from a footer/side-menu "Cookie-Einstellungen" link — pre-fill
    // the toggles from whatever's already stored, so a returning visitor sees their
    // current choices rather than the fresh-visit defaults. The click that triggers
    // this comes from a sibling component (the footer), not this one's own template,
    // so this component's OnPush view never gets scheduled for it on its own —
    // detectChanges() is what actually renders the reopened banner (and makes the
    // settings title available to receive focus).
    this.consentState.reopen$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const stored = this.readStoredConsent();
      const trigger = document.activeElement;
      this.returnFocusTo = trigger instanceof HTMLElement && trigger !== document.body ? trigger : null;
      this.hasDecision = stored !== null;
      this.choices.functional = stored?.functional ?? false;
      this.choices.analytics = stored?.analytics ?? false;
      this.openDetails.clear();
      this.armDecisions();
      this.visible = true;
      this.expanded = true;
      this.cdr.detectChanges();
      this.settingsTitle?.nativeElement.focus();
    });
    // An opt-out preference signal is a legally binding opt-out under CCPA/CPRA
    // (Cal. Code Regs. tit. 11 § 7025) and several other US state laws, and the
    // privacy policy promises we honour it — so it wins over any stored consent
    // and suppresses the banner's non-essential categories entirely.
    if (this.hasOptOutSignal()) {
      this.save(false, false);
      this.visible = false;
      return;
    }

    const consent = this.readStoredConsent();
    if (!consent) {
      this.armDecisions();
      this.visible = true;
      return;
    }
    this.consentState.setFunctional(consent.functional);
    if (consent.analytics) { this.loadGoogleAnalytics(); }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Global Privacy Control, with legacy Do-Not-Track as a fallback signal. */
  private hasOptOutSignal(): boolean {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean; doNotTrack?: string };
    return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
  }

  /** Null when nothing's stored, unreadable, or from a superseded schema version
   *  (all three cases should re-prompt rather than silently assume no consent). */
  private readStoredConsent(): GmCookieConsent | null {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) return null;
      const consent: GmCookieConsent = JSON.parse(raw);
      return consent.v === CONSENT_VERSION ? consent : null;
    } catch {
      return null;
    }
  }

  private armDecisions(): void {
    this.decisionsArmedAt = Date.now() + DECISION_GUARD_MS;
  }

  private decisionsArmed(): boolean {
    return Date.now() >= this.decisionsArmedAt;
  }

  acceptAll(): void {
    if (this.decisionsArmed()) { this.save(true, true); }
  }

  acceptEssential(): void {
    if (this.decisionsArmed()) { this.save(false, false); }
  }

  saveCustom(): void {
    if (this.decisionsArmed()) { this.save(this.choices.functional, this.choices.analytics); }
  }

  showSettings(): void {
    this.armDecisions();
    this.expanded = true;
    this.cdr.detectChanges();
    this.settingsTitle?.nativeElement.focus();
  }

  closeSettings(): void {
    if (this.hasDecision) {
      // Leave the view as-is: switching it back to the compact layout would flash
      // for the duration of the leave animation.
      this.dismiss();
      return;
    }
    this.armDecisions();
    this.openDetails.clear();
    this.expanded = false;
    this.cdr.detectChanges();
    this.customizeBtn?.nativeElement.focus();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible && this.expanded) { this.closeSettings(); }
  }

  onChoiceChange(id: CookieChoiceId, event: Event): void {
    this.choices[id] = (event.target as HTMLInputElement).checked;
  }

  isOpen(id: CookieCategoryId): boolean {
    return this.openDetails.has(id);
  }

  toggleDetails(id: CookieCategoryId): void {
    if (!this.openDetails.delete(id)) { this.openDetails.add(id); }
  }

  private save(functional: boolean, analytics: boolean): void {
    const consent: GmCookieConsent = {
      v: CONSENT_VERSION,
      ts: new Date().toISOString(),
      essential: true,
      functional,
      analytics,
    };
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify(consent)); } catch { /* quota exceeded */ }
    this.consentState.setFunctional(functional);
    if (analytics) { this.loadGoogleAnalytics(); }
    this.dismiss();
  }

  /** Hides the banner and hands focus back to whatever reopened it, so keyboard and
   *  screen-reader users don't lose their place on the page. */
  private dismiss(): void {
    this.visible = false;
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target?.isConnected) { target.focus(); }
  }

  private loadGoogleAnalytics(): void {
    if ((window as any)['gaLoaded']) { return; }
    (window as any)['gaLoaded'] = true;
    (window as any)['dataLayer'] = (window as any)['dataLayer'] || [];
    // eslint-disable-next-line prefer-rest-params
    (window as any)['gtag'] = function () { ((window as any)['dataLayer'] as unknown[]).push(arguments); };
    (window as any)['gtag']('js', new Date());
    (window as any)['gtag']('config', GA_ID);
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);
  }
}
